/**
 * Companio Digital Gift Card form.
 *
 * Ported from the prototype's gift card script, wired to a real /cart/add
 * POST:
 * - the $ amount grid selects a variant (radio semantics, .selected state)
 * - date input is clamped to today-or-later and validated on submit
 * - the 200-character message counter mirrors the prototype
 * - Shopify's timezone offset property is filled in so "Send on" is
 *   interpreted in the buyer's timezone (same trick as Horizon's own
 *   recipient form)
 * - validation shows the prototype's .field-message rows + aria-invalid,
 *   and blocks submission until the form is clean
 *
 * WINDOW E (2026-08-31): the CompanioGiftCard class below is UNCHANGED.
 * The only addition is the delegated gallery listener at the bottom of this
 * file, which drives the new thumbnail column. It is deliberately outside the
 * class so that the checkout-path code above stays provably untouched.
 *
 * The class hardcodes these strings; the markup in
 * sections/companio-gift-card.liquid must keep them exactly:
 *   .amount-button  selected  .gift-field  .field-message  show
 *   [data-gift-card-form] [data-variant-id] [data-timezone-offset]
 *   [data-amount-variant] [data-gift-message] [data-message-count]
 *   input[type="email"]  input[type="date"]
 */

class CompanioGiftCard extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-gift-card-form]');
    if (!this.form) return;

    this.variantInput = this.form.querySelector('[data-variant-id]');
    this.email = this.form.querySelector('input[type="email"]');
    this.date = this.form.querySelector('input[type="date"]');
    this.message = this.form.querySelector('[data-gift-message]');
    this.count = this.form.querySelector('[data-message-count]');

    // Amounts.
    this.addEventListener('click', (event) => {
      const button = event.target.closest('[data-amount-variant]');
      if (!button) return;
      this.querySelectorAll('.amount-button').forEach((other) => {
        other.classList.toggle('selected', other === button);
        other.setAttribute('aria-checked', String(other === button));
      });
      if (this.variantInput) this.variantInput.value = button.dataset.amountVariant;
    });

    // Delivery date: today or later, defaulting to today.
    const today = new Date();
    today.setMinutes(today.getMinutes() - today.getTimezoneOffset());
    const iso = today.toISOString().slice(0, 10);
    if (this.date) {
      this.date.min = iso;
      if (!this.date.value) this.date.value = iso;
    }

    // Buyer timezone offset for Shopify's scheduled delivery.
    const offset = this.form.querySelector('[data-timezone-offset]');
    if (offset) offset.value = String(new Date().getTimezoneOffset() * -1);

    // Message counter.
    if (this.message && this.count) {
      this.message.addEventListener('input', () => {
        this.count.textContent = String(this.message.value.length);
      });
    }

    // Validation.
    this.form.addEventListener('submit', (event) => {
      const emailOk = this.validate(this.email, () => this.email.validity.valid);
      const dateOk = this.validate(this.date, () => {
        return this.date.value !== '' && this.date.value >= this.date.min;
      });
      if (!emailOk || !dateOk) {
        event.preventDefault();
        (emailOk ? this.date : this.email).focus();
      }
    });

    [this.email, this.date].forEach((input) => {
      if (!input) return;
      input.addEventListener('input', () => {
        input.removeAttribute('aria-invalid');
        const message = input.closest('.gift-field')?.querySelector('.field-message');
        if (message) message.classList.remove('show');
      });
    });
  }

  validate(input, check) {
    if (!input) return true;
    const valid = check();
    const message = input.closest('.gift-field')?.querySelector('.field-message');
    if (valid) {
      input.removeAttribute('aria-invalid');
      if (message) message.classList.remove('show');
    } else {
      input.setAttribute('aria-invalid', 'true');
      if (message) message.classList.add('show');
    }
    return valid;
  }
}

if (!customElements.get('companio-gift-card')) {
  customElements.define('companio-gift-card', CompanioGiftCard);
}

/* ------------------------------------------------------------------
   Gallery (added window E, 2026-08-31)

   The artboard draws a thumbnail column beside the artwork. Delegated on
   document so it also works after the theme editor re-renders the section,
   and kept out of the class above so the checkout-path code is untouched.

   The thumbnails are real <button>s, not decorative images sitting next to
   something clickable - people aim at the picture.

   Frames are toggled with the hidden property; the stylesheet declares
   .v2-gc-frame[hidden] { display: none } because hidden loses to a display
   declaration.
   ------------------------------------------------------------------ */
(function () {
  'use strict';

  document.addEventListener('click', function (event) {
    var thumb = event.target.closest ? event.target.closest('[data-gc-thumb]') : null;
    if (!thumb) return;

    var gallery = thumb.closest('[data-gc-gallery]');
    if (!gallery) return;

    var index = thumb.getAttribute('data-gc-thumb');

    gallery.querySelectorAll('[data-gc-thumb]').forEach(function (other) {
      var on = other === thumb;
      other.classList.toggle('is-current', on);
      other.setAttribute('aria-current', on ? 'true' : 'false');
    });

    gallery.querySelectorAll('[data-gc-frame]').forEach(function (frame) {
      frame.hidden = frame.getAttribute('data-gc-frame') !== index;
    });
  });
})();